import type { FC } from 'react';
import React, { useCallback } from 'react';

import { Slider } from '../Slider/Slider';
import { getProgress, getSeekTimeByPercentage } from './ProgressBar.utils';

import styles from './ProgressBar.module.scss';

type ProgressBarProps = {
  size: 'medium' | 'large';
  currentTime: number;
  startTime: number;
  endTime: number;
  loadedPercentage?: number;
  onSeek: (newTime: number) => void;
};

export const ProgressBar: FC<ProgressBarProps> = ({
  size = 'medium',
  currentTime,
  startTime,
  endTime,
  loadedPercentage = 0,
  onSeek,
}) => {
  const progress = getProgress({
    currentTime,
    endTime,
    startTime,
  });

  const handleSeek = useCallback(
    (percentage: number) => {
      const newTime = getSeekTimeByPercentage({
        percentage,
        endTime,
        startTime,
      });
      onSeek(newTime);
    },
    [endTime, onSeek, startTime],
  );

  return (
    <Slider
      additionalElements={
        <div
          className={styles.loadBar}
          style={{ width: `${loadedPercentage}%` }}
        />
      }
      value={progress}
      min={0}
      max={1}
      orientation="horizontal"
      ariaLabel="Seek slider"
      ariaValueMin={startTime}
      ariaValueMax={endTime}
      ariaValueNow={currentTime - startTime}
      ariaValueText={`${Math.round(currentTime - startTime)} of ${Math.round(
        endTime - startTime,
      )} seconds`}
      onChange={handleSeek}
      onClick={handleSeek}
      size={size}
    />
  );
};
